import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, CircularProgress, ToggleButtonGroup, ToggleButton, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete, Snackbar, Alert
} from '@mui/material'
import { PersonAddAlt1, CheckCircleOutline, CancelOutlined } from '@mui/icons-material'
import { FC, useEffect, useState } from 'react'
import { Api, numberWithCommas } from '../../helpers'
import { Connect, mapStateToProps } from '../../Redux'

type OrderStatus = 'Pending' | 'Completed' | 'Cancelled'
type FilterStatus = 'All' | OrderStatus

interface Order {
    _id: string
    productName: string
    sellerName?: string
    customerName: string
    assignedStaffName?: string
    quantity: number
    amount: number
    currency: string
    status: OrderStatus
    date: string
}

interface StaffOption {
    _id: string
    name: string
    phone: string
    active: boolean
}

interface StateProps {
    orders: Order[]
    loading: boolean
    filter: FilterStatus
    assigningOrder: Order | null
    staffOptions: StaffOption[]
    selectedStaff: StaffOption | null
    assigning: boolean
    cancellingOrder: Order | null
    cancelReason: string
    updatingId: string | null
    error: string | null
}

const statusColor: Record<OrderStatus, { bg: string; color: string }> = {
    Pending:   { bg: '#fef9c3', color: '#854d0e' },
    Completed: { bg: '#dcfce7', color: '#166534' },
    Cancelled: { bg: '#fee2e2', color: '#991b1b' },
}

const filters: FilterStatus[] = ['All', 'Pending', 'Completed', 'Cancelled']
const headers = ['Date', 'Product', 'Seller', 'Customer', 'Assigned Staff', 'Qty', 'Amount', 'Status', 'Actions']

const Orders: FC = () => {
    const [state, setState] = useState<StateProps>({
        orders: [],
        loading: true,
        filter: 'All',
        assigningOrder: null,
        staffOptions: [],
        selectedStaff: null,
        assigning: false,
        cancellingOrder: null,
        cancelReason: '',
        updatingId: null,
        error: null
    })
    const { orders, loading, filter, assigningOrder, staffOptions, selectedStaff, assigning, cancellingOrder, cancelReason, updatingId, error } = state

    const getOrders = (status: FilterStatus = 'All') => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'Orders',
            action: 'getOrders',
            ...(status !== 'All' && { status })
        })
            .then((resp: any) => setState(s => ({ ...s, orders: resp, loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const handleFilter = (_: any, val: FilterStatus | null) => {
        if (!val) return
        setState(s => ({ ...s, filter: val }))
        getOrders(val)
    }

    const openAssign = (order: Order) => {
        setState(s => ({ ...s, assigningOrder: order, selectedStaff: null }))
        Api({ controller: 'MarketplaceStaff', action: 'getMarketplaceStaff' })
            .then((resp: any) => setState(s => ({ ...s, staffOptions: (resp || []).filter((m: StaffOption) => m.active) })))
            .catch(() => setState(s => ({ ...s, staffOptions: [] })))
    }

    const confirmAssign = () => {
        if (!assigningOrder || !selectedStaff) return
        setState(s => ({ ...s, assigning: true }))
        Api({ controller: 'Orders', action: 'assignOrderStaff', orderId: assigningOrder._id, staffPhone: selectedStaff.phone })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, assigning: false, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, assigning: false, assigningOrder: null, selectedStaff: null }))
                    getOrders(filter)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, assigning: false, error: err?.message || 'Failed to assign staff member.' })))
    }

    const markCompleted = (order: Order) => {
        setState(s => ({ ...s, updatingId: order._id }))
        Api({ controller: 'Orders', action: 'updateOrderStatus', orderId: order._id, status: 'Completed' })
            .then((resp: any) => {
                setState(s => ({ ...s, updatingId: null, ...(resp?.error && { error: resp.error.message }) }))
                if (!resp?.error) getOrders(filter)
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to update order.' })))
    }

    const confirmCancel = () => {
        if (!cancellingOrder) return
        setState(s => ({ ...s, updatingId: cancellingOrder._id }))
        Api({ controller: 'Orders', action: 'updateOrderStatus', orderId: cancellingOrder._id, status: 'Cancelled', cancelReason })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, updatingId: null, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, updatingId: null, cancellingOrder: null, cancelReason: '' }))
                    getOrders(filter)
                }
            })
            .catch((err: any) => setState(s => ({ ...s, updatingId: null, error: err?.message || 'Failed to cancel order.' })))
    }

    useEffect(() => { getOrders() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Orders</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Assign a Marketplace Staff member to each order - they handle all buyer communication, never the seller.
                </Typography>
            </Box>

            {/* Filter */}
            <Box sx={{ px: '5px', mb: 2 }}>
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={handleFilter}
                    size='small'
                    sx={{ bgcolor: '#fff', borderRadius: 2 }}
                >
                    {filters.map(f => (
                        <ToggleButton
                            key={f}
                            value={f}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2.5,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }
                            }}
                        >
                            {f}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ mx: '5px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                {headers.map(h => (
                                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                        No {filter === 'All' ? '' : filter.toLowerCase()} orders found.
                                    </TableCell>
                                </TableRow>
                            ) : orders.map(order => {
                                const { _id, date, productName, sellerName, customerName, assignedStaffName, quantity, amount, currency, status } = order
                                return (
                                    <TableRow key={_id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.85rem' }}>
                                            {new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{productName}</TableCell>
                                        <TableCell>{sellerName || '—'}</TableCell>
                                        <TableCell>{customerName}</TableCell>
                                        <TableCell>
                                            {assignedStaffName
                                                ? <Chip label={assignedStaffName} size='small' sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600 }} />
                                                : <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>Unassigned</Typography>}
                                        </TableCell>
                                        <TableCell>{numberWithCommas(quantity)}</TableCell>
                                        <TableCell>{currency} {numberWithCommas(amount)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={status}
                                                size='small'
                                                sx={{
                                                    bgcolor: statusColor[status].bg,
                                                    color: statusColor[status].color,
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {status === 'Pending' && (
                                                <>
                                                    <Tooltip title={assignedStaffName ? 'Reassign Staff' : 'Assign Staff'}>
                                                        <IconButton size='small' onClick={() => openAssign(order)} sx={{ color: 'primary.main' }}>
                                                            <PersonAddAlt1 fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title='Mark Completed'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => markCompleted(order)}
                                                            disabled={updatingId === _id}
                                                            sx={{ color: '#16a34a', ml: 0.5 }}
                                                        >
                                                            {updatingId === _id ? <CircularProgress size={14} color='inherit' /> : <CheckCircleOutline fontSize='small' />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title='Cancel Order'>
                                                        <IconButton
                                                            size='small'
                                                            onClick={() => setState(s => ({ ...s, cancellingOrder: order, cancelReason: '' }))}
                                                            disabled={updatingId === _id}
                                                            sx={{ color: '#ef4444', ml: 0.5 }}
                                                        >
                                                            <CancelOutlined fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Assign Staff Dialog */}
            <Dialog open={Boolean(assigningOrder)} onClose={() => !assigning && setState(s => ({ ...s, assigningOrder: null }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Assign Staff Member</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Autocomplete
                        options={staffOptions}
                        getOptionLabel={(o) => o.name}
                        value={selectedStaff}
                        onChange={(_, val) => setState(s => ({ ...s, selectedStaff: val }))}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        noOptionsText='No active staff members'
                        renderOption={(props, o) => (
                            <li {...props} key={o._id}>
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.name}</Typography>
                                    <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.phone}</Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label='Staff Member' placeholder='Search staff...' />
                        )}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, assigningOrder: null }))} disabled={assigning}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={confirmAssign}
                        disabled={!selectedStaff || assigning}
                        startIcon={assigning ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Order Dialog */}
            <Dialog open={Boolean(cancellingOrder)} onClose={() => !updatingId && setState(s => ({ ...s, cancellingOrder: null }))} fullWidth maxWidth='xs'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Cancel Order</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <TextField
                        label='Reason for cancellation'
                        value={cancelReason}
                        onChange={e => setState(s => ({ ...s, cancelReason: e.target.value }))}
                        fullWidth
                        size='small'
                        multiline
                        rows={2}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, cancellingOrder: null }))} disabled={Boolean(updatingId)}>Back</Button>
                    <Button
                        variant='contained'
                        color='error'
                        onClick={confirmCancel}
                        disabled={!cancelReason.trim() || Boolean(updatingId)}
                        startIcon={updatingId ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Confirm Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Orders)
